// React
import { useContext } from "react"

// Components
import { ActionButton, NumberInput } from "components"

// Form
import { Controller, useForm, useFormContext } from "react-hook-form"
import { yupResolver } from "@hookform/resolvers/yup"

// Contexts
import { RefundDetailContext } from "../contexts"

// Types
import type { DetailType, FormType } from "../utils"

// Utils
import { convertNumber, useToggle } from "utils"
import { detailValidationSchema } from "../utils"

export function DetailRefund(): JSX.Element {
  // Hooks
  const { item } = useContext(RefundDetailContext)
  const { isActive, toggle } = useToggle(false)

  // Vars
  const qty_back_returned = item.qty_back_returned
  const qty_return_item = item.qty_return_item

  // Form
  const { formState: { disabled } } = useFormContext<FormType>()

  if (isActive) {
    return <FormSection toggle={toggle} />
  }

  const total: number = item.price_refund * (item.qty_return_item ?? 0)
  const totalAfterDiscount: number = total - (total *item.discount / 100)

  return (
    <tr>
      <td className="text-right">{convertNumber(qty_back_returned).intoCurrency}</td>
      <td className="text-right">{convertNumber(qty_return_item).intoCurrency}</td>
      <td className="text-right">{convertNumber(qty_back_returned - qty_return_item).intoCurrency}</td>
      <td>{item.unit_sell_measure_name}</td>
      <td className="text-center">{item.product_barcode}</td>
      <td>{item.product_name}</td>
      <td className="text-right">{convertNumber(item.price_refund).intoCurrency}</td>
      <td className="text-right">{convertNumber(item.discount).intoCurrency}%</td>
      <td className="text-right">{convertNumber(totalAfterDiscount).intoCurrency}</td>
      <td>{item.tax_name} ({item.tax_rate}%)</td>

      <td>
        {!disabled && (
          <section className="flex justify-center">
            <ActionButton.Update onClick={toggle} />
          </section>
        )}
      </td>
    </tr>
  )
}

function FormSection(params: {
  toggle: () => void
}): JSX.Element {
  // Hooks
  const { item, onUpdate } = useContext(RefundDetailContext)

  // Vars
  const qty_back_returned = item.qty_back_returned
  const toggle = params.toggle

  // Form
  const { control, handleSubmit, watch } = useForm({
    defaultValues: item,
    resolver: yupResolver(detailValidationSchema)
  })

  const total = Number(watch('qty_return_item')*item.price_refund)
  const totalAfterDiscount: number = total - (total *item.discount / 100)
  const totalTax = item.tax_rate? (totalAfterDiscount * item.tax_rate /100):0

  const onSubmit = (value: DetailType) => {
    toggle()
    onUpdate({
      ...item,
      qty_return_item: value.qty_return_item,
      total: totalAfterDiscount,
      total_tax_collect:totalTax,
    })
  }

  return (
    <tr>
      <td className="text-right">{convertNumber(qty_back_returned).intoCurrency}</td>

      <Controller
        name="qty_return_item"
        control={control}
        render={({ field, fieldState }) => (
          <>
            <td className="text-right">
              <NumberInput
                inputClass="input-sm"
                error={fieldState.error?.message}
                value={field.value}
                ref={field.ref}
                onValueChange={res => field.onChange(res.floatValue)}
              />
            </td>

            <td className="text-right">{convertNumber(qty_back_returned - field.value).intoCurrency}</td>
          </>
        )}
      />

      <td>{item.unit_convention_name}</td>
      <td className="text-center">{item.product_barcode}</td>
      <td>{item.product_name}</td>
      <td className="text-right">{convertNumber(item.price_refund).intoCurrency}</td>
      <td className="text-right">{convertNumber(item.discount).intoCurrency}%</td>
      <td className="text-right">{convertNumber(totalAfterDiscount).intoCurrency}</td>
      <td>{item.tax_name} ({item.tax_rate}%)</td>

      <td>
        <section className="flex justify-center">
          <ActionButton.Check onClick={handleSubmit(onSubmit)} />
          <ActionButton.Close onClick={toggle} />
        </section>
      </td>
    </tr>
  )
}